import { localStorage as safeLocalStorage } from 'utils/browserStorage';
import React, { useCallback } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import HomeIcon from '@mui/icons-material/Home';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import DescriptionIcon from '@mui/icons-material/Description';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import SearchIcon from '@mui/icons-material/Search';
import SchoolIcon from '@mui/icons-material/School';
import styles from '../DentistNavbar/DentistNavbar.module.scss';
import DentistSideMenuHeader from './DentistSideMenuHeader';
import { useOfficeCeDirectoryAccess } from '../../pages/dentist/OfficeCeCoursesPage/useOfficeCeDirectoryAccess';

export interface DentistSideMenuProps {
    isOpen: boolean;
    onClose: () => void;
}

/**
 * Standalone slide-out side menu for DO surfaces. Extracted from
 * `DentistNavbar` so pages that already render their own header
 * (LocationsPage, InvoicesPage, etc.) can reuse the menu without
 * duplicating the navbar logo + action buttons row. Triggered by a
 * hamburger button placed wherever the page wants.
 */
const DentistSideMenu: React.FC<DentistSideMenuProps> = ({ isOpen, onClose }) => {
    const history = useHistory();
    const location = useLocation();
    const ceDirectoryAccess = useOfficeCeDirectoryAccess();

    const handleHireStaff = useCallback(() => {
        onClose();
        (window as any).TempStars?.App?.goReact('jobBoard');
    }, [onClose]);

    return (
        <>
            {/* Side Menu */}
            <div className={`${styles.sideMenu} ${isOpen ? styles.open : ''}`}>
                <DentistSideMenuHeader />
                <nav className={styles.sideMenuNav}>
                    <a
                        href="#"
                        className={`${styles.menuItem} ${location.pathname.includes('dentistHome') ? styles.active : ''}`}
                        onClick={(e) => { e.preventDefault(); onClose(); history.push({ pathname: 'dentistHome' }); }}
                    >
                        <HomeIcon />
                        <span>Home</span>
                    </a>
                    <a
                        href="#"
                        className={`${styles.menuItem} ${location.pathname.includes('dentistLocations') ? styles.active : ''}`}
                        onClick={(e) => { e.preventDefault(); onClose(); (window as any).TempStars?.App?.goReact('dentistLocations'); }}
                    >
                        <LocationOnIcon />
                        <span>Locations</span>
                    </a>
                    <a
                        href="#"
                        className={`${styles.menuItem} ${location.pathname.includes('dentistInvoices') ? styles.active : ''}`}
                        onClick={(e) => { e.preventDefault(); onClose(); (window as any).TempStars?.App?.goReact('dentistInvoices'); }}
                    >
                        <DescriptionIcon />
                        <span>Invoices</span>
                    </a>
                    {ceDirectoryAccess.enabled && (
                        <a
                            href="#"
                            className={`${styles.menuItem} ${location.pathname.includes('dentistCeCourses') ? styles.active : ''}`}
                            onClick={(e) => {
                                e.preventDefault();
                                onClose();
                                const goReact = (window as any).TempStars?.App?.goReact;
                                if (typeof goReact === 'function') goReact('dentistCeCourses');
                                else history.push({ pathname: 'dentistCeCourses' });
                            }}
                        >
                            <SchoolIcon />
                            <span>CE Courses</span>
                        </a>
                    )}
                    <a
                        href="#"
                        className={`${styles.menuItem} ${location.pathname.includes('jobBoard') || location.pathname.includes('hireStaff') ? styles.active : ''}`}
                        onClick={(e) => { e.preventDefault(); handleHireStaff(); }}
                    >
                        <WorkOutlineIcon />
                        <span>Hire Staff</span>
                    </a>
                    <a
                        href="#"
                        className={`${styles.menuItem} ${location.pathname.includes('dentistContactSupport') ? styles.active : ''}`}
                        onClick={(e) => { e.preventDefault(); onClose(); history.push({ pathname: 'dentistContactSupport' }); }}
                    >
                        <HelpOutlineIcon />
                        <span>Support</span>
                    </a>

                    {(() => {
                        const ts: any = (window as any).TempStars;
                        const user = ts?.User?.getCurrentUser?.();
                        const isTest = Array.isArray(user?.locations)
                            && user.locations.some((loc: any) => Number(loc?.isTest) === 1);
                        if (!isTest) return null;
                        return (
                            <a
                                href="#"
                                className={styles.menuItem}
                                onClick={(e) => {
                                    e.preventDefault();
                                    onClose();
                                    try { (window as any).TempStars?.App?.goReact('MixpanelLogs'); }
                                    catch { history.push({ pathname: 'MixpanelLogs' }); }
                                }}
                            >
                                <SearchIcon />
                                <span>MixPanel Logs</span>
                                <span className={styles.testOnly}>(Test Only)</span>
                            </a>
                        );
                    })()}

                    <a
                        href="#"
                        className={`${styles.menuItem} ${styles.logOutItem}`}
                        onClick={(e) => {
                            e.preventDefault();
                            onClose();
                            if ((window as any).TempStars?.User?.logout) {
                                (window as any).TempStars.User.logout()
                                    .then(() => { window.location.href = '/'; })
                                    .catch(() => { safeLocalStorage.clear(); window.location.href = '/'; });
                            } else {
                                safeLocalStorage.clear();
                                window.location.href = '/';
                            }
                        }}
                    >
                        <ExitToAppIcon />
                        <span>Log Out</span>
                    </a>
                </nav>
            </div>

            {/* Overlay */}
            {isOpen && (
                <div className={styles.overlay} onClick={onClose} aria-label="Close menu" />
            )}
        </>
    );
};

export default DentistSideMenu;
